import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, Package, Users, MessageSquare, Calendar, FileText,
  CreditCard, FolderOpen, Percent, Image, Newspaper, Truck, CalendarRange, RotateCcw,
  ShieldAlert, Bell, Globe, Images, Handshake, Settings, UserCog, Wrench, ExternalLink, Menu,
  CalendarHeart, Mail, MapPin, Star,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Logo } from "../brand/Logo";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

export const ADMIN_NAV = [
  {
    section: null,
    items: [{ to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, ref: "B01" }],
  },
  {
    section: "Gestion",
    items: [
      { to: "/admin/reservations", label: "Commandes & Réservations", icon: ShoppingBag, ref: "B02" },
      { to: "/admin/catalogue", label: "Catalogue & Stock", icon: Package, ref: "B04" },
      { to: "/admin/clients", label: "Clients", icon: Users, ref: "B11" },
      { to: "/admin/coordination", label: "Coordination", icon: CalendarHeart, ref: "Coordination" },
      { to: "/admin/messages", label: "Messages", icon: MessageSquare, ref: "B12" },
      { to: "/admin/rendez-vous", label: "Rendez-vous", icon: Calendar, ref: "B13" },
      { to: "/admin/devis", label: "Devis", icon: FileText, ref: "B14" },
      { to: "/admin/paiements", label: "Paiements & Échéances", icon: CreditCard, ref: "B15" },
      { to: "/admin/documents", label: "Documents & CGV", icon: FolderOpen, ref: "B16" },
      { to: "/admin/codes-promo", label: "Codes promo & Remises", icon: Percent, ref: "B17" },
    ],
  },
  {
    section: "Logistique",
    items: [
      { to: "/admin/livraisons", label: "Livraisons / Retraits", icon: Truck, ref: "B08" },
      { to: "/admin/planning", label: "Planning matériel", icon: CalendarRange, ref: "B07" },
      { to: "/admin/retours", label: "Retours & état du matériel", icon: RotateCcw, ref: "B09" },
      { to: "/admin/cautions", label: "Cautions & Incidents", icon: ShieldAlert, ref: "B10" },
      { to: "/admin/alertes", label: "Alertes disponibilité", icon: Bell, ref: "B07" },
    ],
  },
  {
    section: "Contenu",
    items: [
      { to: "/admin/realisations", label: "Réalisations & Galerie", icon: Image, ref: "B18" },
      { to: "/admin/blog", label: "Blog & Conseils", icon: Newspaper, ref: "B20" },
      { to: "/admin/pages", label: "Pages du site", icon: Globe, ref: "A01-A25" },
      { to: "/admin/mediatheque", label: "Médiathèque", icon: Images, ref: "11 bis" },
      { to: "/admin/partenaires", label: "Partenaires", icon: Handshake, ref: "B19" },
      { to: "/admin/lieux", label: "Lieux de réception", icon: MapPin, ref: "Lieux" },
      { to: "/admin/avis", label: "Avis clients", icon: Star, ref: "Avis" },
      { to: "/admin/formules-coordination", label: "Formules coordination", icon: CalendarHeart, ref: "CMS" },
    ],
  },
  {
    section: "Réglages",
    items: [
      { to: "/admin/reglages", label: "Réglages du site", icon: Settings, ref: "B21" },
      { to: "/admin/emails", label: "E-mails transactionnels", icon: Mail, ref: "6A" },
      { to: "/admin/utilisateurs", label: "Utilisateurs & droits", icon: UserCog, ref: "B22" },
      { to: "/admin/parametres", label: "Paramètres techniques", icon: Wrench, ref: "B23" },
      { to: "/admin/notifications", label: "Centre de notifications", icon: Bell, ref: "B24" },
    ],
  },
];

const SidebarContent = ({ onNavigate, user }) => {
  // B22 : masquage des entrées de menu sans permission « read » (super_admin voit tout)
  const isSuper = user?.role === "super_admin";
  const can = (module) => isSuper || ["read", "write", "full"].includes((user?.permissions || {})[module]);
  const ROUTE_PERM = {
    "/admin/reservations": "reservations", "/admin/catalogue": "catalog", "/admin/clients": "customers",
    "/admin/coordination": "coordination", "/admin/messages": "messages", "/admin/rendez-vous": "appointments",
    "/admin/devis": "quotes", "/admin/paiements": "payments", "/admin/documents": "customers",
    "/admin/codes-promo": "payments", "/admin/livraisons": "logistics", "/admin/planning": "logistics",
    "/admin/retours": "logistics", "/admin/cautions": "logistics", "/admin/alertes": "logistics",
    "/admin/realisations": "content", "/admin/blog": "content", "/admin/pages": "content",
    "/admin/mediatheque": "content", "/admin/partenaires": "content", "/admin/formules-coordination": "content",
    "/admin/lieux": "content", "/admin/reglages": "settings", "/admin/emails": "settings",
  };
  const SUPER_ONLY = new Set(["/admin/utilisateurs", "/admin/parametres"]);
  const nav = ADMIN_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (SUPER_ONLY.has(item.to)) return isSuper;
      const module = ROUTE_PERM[item.to];
      return !module || can(module);
    }),
  })).filter((group) => group.items.length > 0);
  return (
  <div className="flex h-full flex-col">
    <div className="border-b border-cream/10 px-5 py-5">
      <Logo dark compact />
      <p className="mt-1 text-[10px] uppercase tracking-[0.35em] text-sage">Administration</p>
    </div>
    <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Navigation administration" data-testid="admin-sidebar-nav">
      {nav.map((group, gi) => (
        <div key={gi} className="mb-4">
          {group.section && (
            <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-cream/40">{group.section}</p>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`admin-nav-${item.to.replace("/admin/", "").replace("/admin", "dashboard")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-colors ${
                      isActive ? "bg-sage/25 text-cream" : "text-cream/65 hover:bg-cream/5 hover:text-cream"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-[10px] font-bold text-deepgreen">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
    <div className="border-t border-cream/10 p-3">
      <Link to="/" data-testid="admin-view-site-link" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium text-cream/65 transition-colors hover:bg-cream/5 hover:text-cream">
        <ExternalLink className="h-4 w-4" /> Voir le site
      </Link>
      <div className="mt-2 flex items-center gap-3 rounded-lg bg-cream/5 px-3 py-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sage text-xs font-bold text-deepgreen">T</div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-cream">Compte administrateur</p>
          <p className="text-[10px] text-cream/50">Art Event</p>
        </div>
      </div>
    </div>
  </div>
  );
};

export const AdminLayout = () => {
  const [open, setOpen] = useState(false);
  // v1.6 : compteur de la cloche synchronisé sur les notifications ACTIVES non lues (jamais résolues/archivées)
  const [notifCount, setNotifCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin/notifications").then((r) => setNotifCount(r.data.filter((n) => !n.read && !n.resolved && !n.archived).length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const { user } = useAuth();
  return (
    <div className="flex min-h-screen bg-cream-dark">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-deepgreen lg:block" data-testid="admin-sidebar">
        <SidebarContent user={user} />
      </aside>
      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-white/90 px-4 backdrop-blur-lg lg:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="rounded-lg p-2 text-deepgreen lg:hidden" data-testid="admin-mobile-menu-button" aria-label="Ouvrir le menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-deepgreen p-0">
              <SidebarContent onNavigate={() => setOpen(false)} user={user} />
            </SheetContent>
          </Sheet>
          <div className="flex-1" />
          <Link to="/admin/notifications" data-testid="admin-header-notifications" className="relative rounded-full p-2 text-deepgreen hover:bg-sage-light" aria-label="Notifications">
            <Bell className="h-5 w-5" />
            {notifCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[9px] font-bold text-deepgreen" data-testid="admin-notif-count">{notifCount}</span>
            )}
          </Link>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
